import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { Building2, Calendar, History, QrCode, Tag } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import client from "../../../../client";
import { formatNumber, getCurrencySymbolFromCache } from "../../../../utils";
import { getLocalisedName } from "../../../../utils/i18n";
import { formatUnitPrice } from "../../../RepresentativeOffers/utils";
import { Carousel } from "../Carousel";
import { CommodityEvent, Deposit } from "../deposit-dtos";
import { generateTraceabilityPdf } from "./generateTraceabilityPdf";
import { formatDepositDate } from "../../utils";

interface DepositDetailsProps {
  deposit: Deposit;
  onMakeOffer?: (deposit: Deposit) => void;
  onClickAggregatorDetails?: (id: number) => void;
  isOpen: boolean;
  variant?: "modal" | "page";
}

interface DepositDetailsModalProps {
  deposit: Deposit;
  isOpen: boolean;
  onClose: () => void;
  onMakeOffer?: (deposit: Deposit) => void;
  onClickAggregatorDetails?: (id: number) => void;
}

interface TimelineEntry {
  key: string;
  date: string;
  title: string;
  details?: string;
}

const DEPOSIT_CREATED_EVENT_CODE = "deposit_created";

export const DepositDetails = ({
  deposit,
  onMakeOffer,
  onClickAggregatorDetails,
  isOpen,
  variant = "modal",
}: DepositDetailsProps) => {
  const { t } = useTranslation();

  const depositUuid = deposit?.uuid;
  const produceUuid = deposit?.farmer_produce?.uuid;

  const [events, setEvents] = useState<CommodityEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(Boolean(isOpen && produceUuid));
  const [eventsError, setEventsError] = useState(false);

  useEffect(() => {
    if (!isOpen || !produceUuid) {
      return;
    }

    setEventsLoading(true);
    setEventsError(false);

    let active = true;

    const fetchEvents = async () => {
      try {
        const response = await client.get(`api/farmer_produces/${produceUuid}/events/`, {
          params: { deposit: depositUuid },
        });
        const data = response.data?.results ?? response.data;
        if (active) {
          setEvents(Array.isArray(data) ? data : []);
        }
      } catch {
        if (active) {
          setEventsError(true);
        }
      } finally {
        if (active) {
          setEventsLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      active = false;
    };
  }, [produceUuid, depositUuid, isOpen]);

  if (!deposit) {
    return null;
  }

  const currencySymbol = getCurrencySymbolFromCache() ?? "";
  const unitCode = deposit.unit_of_measure?.code;
  const offerNumber =
    (!!deposit.direct_offers && deposit.direct_offers.length) + (deposit.market_order_id ? 1 : 0);
  const collectionTime = deposit.collection_time ?? deposit.backend_creation_time;

  const formatDate = (date: string) =>
    formatDepositDate(date, {
      today: t("farmersMarket.depositCard.relativeDate.today"),
      yesterday: t("farmersMarket.depositCard.relativeDate.yesterday"),
    });

  const produceEvents = produceUuid ? events : [];
  const timeline: TimelineEntry[] = produceEvents.map(event => ({
    key: event.uuid,
    date: event.event_date,
    title: event.event_type_name,
    details: event.details,
  }));
  if (!produceEvents.some(event => event.event_type_code === DEPOSIT_CREATED_EVENT_CODE)) {
    timeline.push({
      key: deposit.uuid,
      date: collectionTime,
      title: t("farmersMarket.depositCard.depositCreatedEvent"),
      details: t("farmersMarket.depositCard.depositCreatedEventDetails", {
        quantity: formatNumber(deposit.original_quantity ?? deposit.quantity),
        unit: getLocalisedName(deposit.unit_of_measure),
        grade: getLocalisedName(deposit.quality_grade),
      }),
    });
  }
  timeline.sort((a, b) => parseISO(b.date).getTime() - parseISO(a.date).getTime());

  const details: { label: string; value: ReactNode }[] = [
    {
      label: t("farmersMarket.details.columns.commodityName"),
      value: getLocalisedName(
        deposit.farmer_produce?.commodity || deposit.commodity || "Unknown commodity"
      ),
    },
    {
      label: t("farmersMarket.details.columns.depositQuality"),
      value: deposit.quality_grade?.name || "Unknown quality",
    },
    {
      label: t("farmersMarket.details.columns.depositQuantity"),
      value: unitCode ? `${formatNumber(deposit.quantity)} ${unitCode}` : "",
    },
    {
      label: t("farmersMarket.details.columns.depositPrice"),
      value: unitCode ? formatUnitPrice(deposit.price, currencySymbol, unitCode) : "",
    },
  ];

  const stats: { label: string; icon: typeof Calendar; value: ReactNode }[] = [
    {
      label: t("farmersMarket.depositCard.depositDate"),
      icon: Calendar,
      value: formatDate(collectionTime),
    },
    {
      label: t("farmersMarket.details.columns.offers"),
      icon: Tag,
      value: (
        <Badge variant={offerNumber ? "warning" : "default"}>
          {offerNumber
            ? `${offerNumber} ${t("farmersMarket.details.status.active")}`
            : t("farmersMarket.details.status.noOffersYet")}
        </Badge>
      ),
    },
    {
      label: t("farmersMarket.depositCard.aggregationCentre"),
      icon: Building2,
      value: onClickAggregatorDetails ? (
        <button
          type="button"
          className="cursor-pointer font-medium text-primary underline-offset-2 hover:underline"
          onClick={() => onClickAggregatorDetails(deposit.aggregator?.id)}
        >
          {deposit.aggregator?.name}
        </button>
      ) : (
        deposit.aggregator?.name
      ),
    },
  ];

  const actionButtons = (
    <div className="flex flex-wrap gap-2">
      {onMakeOffer && !deposit.has_direct_offer_same_trader && (
        <WFPButton onClick={() => onMakeOffer(deposit)}>
          {t("farmersMarket.depositCard.makeOffer")}
        </WFPButton>
      )}
      <WFPButton variant="secondary" onClick={() => generateTraceabilityPdf(deposit, t)}>
        <QrCode className="size-4" />
        {t("farmersMarket.depositCard.traceabilityCode")}
      </WFPButton>
    </div>
  );

  const isPage = variant === "page";

  return (
    <div className={cn(isPage && "mx-auto max-w-4xl", "p-2")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold mb-2">
            {getLocalisedName(deposit.farmer_produce?.commodity || deposit.commodity)}
          </h2>
          <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {t("farmersMarket.depositCard.deposit")} #{deposit.id} | {formatDate(collectionTime)}
          </div>
        </div>
        {!isPage && actionButtons}
      </div>

      <Separator className="my-4" />

      <div className="flex flex-col gap-4">
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <Carousel className="h-56 w-full" imageUrls={deposit.picture_urls || []} />
            {isPage && actionButtons}
          </div>

          <Card className="gap-0 py-0">
            <CardContent className="divide-y divide-border px-0">
              {details.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-4 px-6 py-3">
                  <span className="text-muted-foreground text-sm">{label}</span>
                  <span className="font-medium text-foreground text-sm">{value}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="grid divide-y divide-border px-0 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {stats.map(({ label, icon: Icon, value }) => (
              <div key={label} className="flex items-center gap-3 px-6 py-4">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="size-4" />
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground text-sm">{label}</span>
                  <span className="font-medium text-foreground text-sm">{value}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="size-4 text-primary" />
              {t("farmersMarket.depositCard.eventHistory")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eventsLoading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Spinner className="size-4" />
                {t("farmersMarket.depositCard.eventsLoading")}
              </div>
            )}
            {eventsError && (
              <div className="text-destructive text-sm">
                {t("farmersMarket.depositCard.eventsError")}
              </div>
            )}
            {!eventsLoading && !eventsError && (
              <ol className="flex max-h-64 flex-col overflow-y-auto">
                {timeline.map((entry, index) => (
                  <li key={entry.key} className="flex gap-3">
                    <div className="relative flex w-4 shrink-0 justify-center">
                      {index !== timeline.length - 1 && (
                        <span className="absolute top-4 bottom-0 w-px bg-border" />
                      )}
                      <span className="relative top-4 size-2.5 shrink-0 rounded-full bg-primary" />
                    </div>
                    <div
                      className={cn(
                        "grid flex-1 grid-cols-[96px_1fr] gap-4 py-3",
                        index !== timeline.length - 1 && "border-border border-b"
                      )}
                    >
                      <div className="text-muted-foreground text-sm">
                        {format(parseISO(entry.date), "d MMM yyyy")}
                      </div>
                      <div>
                        <div className="font-semibold text-foreground text-sm">{entry.title}</div>
                        {entry.details && (
                          <div className="text-muted-foreground text-sm">{entry.details}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const DepositDetailsModal = ({
  deposit,
  isOpen,
  onClose,
  onMakeOffer,
  onClickAggregatorDetails,
}: DepositDetailsModalProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader className="sr-only">
          <DialogTitle>{t("farmersMarket.details.depositDetails")}</DialogTitle>
        </DialogHeader>
        <DepositDetails
          deposit={deposit}
          onMakeOffer={onMakeOffer}
          onClickAggregatorDetails={onClickAggregatorDetails}
          isOpen={isOpen}
        />
      </DialogContent>
    </Dialog>
  );
};
